import React from 'react';
import {
  addDays,
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  format,
  isBefore,
  startOfDay,
  startOfMonth,
  subDays,
  subHours,
  subMonths,
} from 'date-fns';
import { UTCDate } from '@date-fns/utc';

import { OmaLummeApp, ConsumptionApp } from '../../omaLummeContext';

import { pastEstimateDays } from '../Graphs/ReportingGraph/ReportingGraph';
// Actions.
import {
  fetchConsumptionAverage,
  fetchConsumptionRange,
  fetchEliqEuc,
  fetchEliqForecast,
  fetchEliqHomeProfile,
  fetchEliqHomeProfileCompletion,
  fetchEliqSimilarHomes,
  fetchEliqSimilarHomesConsumption,
  fetchEliqSimilarHomesReport,
  fetchNordPoolSpotData,
  fetchWeightedAverage,
  getWeatherAndForecast,
} from '../../actions';
// Types.
import { WrapperProps } from '../../types/context';
import { WeatherDataV2 } from '../../types/contract';
import { ConsumptionAverage, ConsumptionRates, WeightedAvgSpotPriceItem } from '../../types/consumptionRates';
import { EliqEuc, EliqForecast, EliqHomeProfile, EliqSimilarHomes, EliqSimilarHomesConsumption, EliqSimilarHomesReport } from '../../types/eliq';
import { NordPoolSpotGraphDatapoint } from '../../types/graphs';
// Utils.
import { generateAddress } from '../../utils/address';

type GraphControls = {
  activeFilter: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  meteringPointId: string;
  contractCode: string;
  meteringPointGroup?: string;
  dayConsumptionAs15min?: boolean;
};

/**
 * Preload consumption page data
 */
const ConsumptionPageWrapper: React.FunctionComponent<WrapperProps> = (props: WrapperProps) => {
  const dispatch = OmaLummeApp.useDispatch();
  const dispatchConsumption = ConsumptionApp.useDispatch();

  const { basicData, config, users, selectedMeteringPoint } = OmaLummeApp.useState();
  const { activeFilter, startDate, endDate, type, nordPoolData, homeProfileCompletion, dayConsumptionAs15min } = ConsumptionApp.useState();
  const allContracts = OmaLummeApp.useState().contracts;
  const { children } = props;

  const [consumption, setConsumption] = React.useState<ConsumptionRates>();
  const [consumptionLoading, setConsumptionLoading] = React.useState<boolean>(false);
  const [forecast, setForecast] = React.useState<EliqForecast>();
  const [forecastLoading, setForecastLoading] = React.useState<boolean>(false);
  const [weather, setWeather] = React.useState<WeatherDataV2>();
  const [weatherLoading, setWeatherLoading] = React.useState<boolean>(false);
  const [spotData, setSpotData] = React.useState<NordPoolSpotGraphDatapoint[]>();
  const [spotDataLoading, setSpotDataLoading] = React.useState<boolean>(false);

  const [eliqHomeProfileCompletion, setEliqHomeProfileCompletion] = React.useState<number>();
  const [eliqHomeProfile, setEliqHomeProfile] = React.useState<EliqHomeProfile>();
  const [eliqEucData, setEliqEucData] = React.useState<EliqEuc>();
  const [eliqSh, setEliqSh] = React.useState<EliqSimilarHomes>();
  const [eliqShc, setEliqShc] = React.useState<EliqSimilarHomesConsumption>();
  const [eliqShr, setEliqShr] = React.useState<EliqSimilarHomesReport>();
  const [weightedAvgSpotPrice, setWeightedAvgSpotPrice] = React.useState<WeightedAvgSpotPriceItem[]>();
  const [weightedAvgSpotPriceLoading, setWeightedAvgSpotPriceLoading] = React.useState<boolean>(false);
  const [consumptionAvg, setConsumptionAvg] = React.useState<ConsumptionAverage>();
  const [consumptionAvgLoading, setConsumptionAvgLoading] = React.useState<boolean>(false);

  const { activeUser } = users;

  const basicContract = basicData
    ? basicData.find((mp) => mp.GSRN === selectedMeteringPoint?.meteringPointId && mp.contractNo === selectedMeteringPoint.contractNo.toString())
    : undefined;

  const meteringPointType = basicContract && basicContract.meteringpointType ? (basicContract.meteringpointType.toLowerCase() ?? '') : '';
  const productionContract = meteringPointType === 'production';
  const b2bUser = activeUser.customerType === 'business';

  const [prevControls, setPrevControls] = React.useState<GraphControls>({
    activeFilter,
    startDate,
    endDate,
    meteringPointId: '',
    contractCode: '',
    dayConsumptionAs15min,
  });

  const loadMeteringPoints = React.useCallback(async () => {
    if (activeUser.userId === 0 || !allContracts) {
      return;
    }

    if (basicContract) {
      if (startDate && startDate < new Date(basicContract.contractStartDate)) {
        dispatchConsumption(['changeStartDate', new Date(basicContract.contractStartDate)]);
      }

      if (basicContract.contractEndDate && endDate && endDate > new Date(basicContract.contractEndDate)) {
        dispatchConsumption(['changeEndDate', new Date(basicContract.contractEndDate)]);

        if (startDate && startDate > new Date(basicContract.contractEndDate)) {
          dispatchConsumption(['changeStartDate', subMonths(new Date(basicContract.contractEndDate), 1)]);
        }
      }

      dispatch([
        'changeSelectedMeteringPoint',
        {
          customerId: parseInt(basicContract.customerNo),
          meteringPointId: basicContract.GSRN,
          contractCode: basicContract.contractCode,
          contractNo: parseInt(basicContract.contractNo),
        },
      ]);
    }
  }, [activeUser, allContracts]);

  React.useEffect(() => {
    loadMeteringPoints();
  }, [activeUser.userId, loadMeteringPoints]);

  React.useEffect(() => {
    const lastContractEndDate = basicContract && basicContract.contractEndDate ? basicContract.contractEndDate : new Date();

    const monthStart = isBefore(startOfMonth(new Date(lastContractEndDate)), startOfMonth(new Date()))
      ? startOfMonth(new Date(lastContractEndDate))
      : startOfMonth(new Date());
    const monthEnd = isBefore(startOfMonth(new Date(lastContractEndDate)), startOfMonth(new Date()))
      ? endOfMonth(new Date(lastContractEndDate))
      : endOfMonth(new Date());

    dispatchConsumption(['changeActiveFilter', 'month']);
    dispatchConsumption(['changeStartDate', monthStart]);
    dispatchConsumption(['changeEndDate', monthEnd]);
  }, [basicContract, selectedMeteringPoint]);

  React.useEffect(() => {
    async function fetchConsumptionData(): Promise<void> {
      setConsumptionLoading(true);

      let viewPeriod = 'Month';
      if (activeFilter === 'year') {
        viewPeriod = 'Year';
      }
      if (activeFilter === 'day') {
        viewPeriod = dayConsumptionAs15min ? 'Hour' : 'Day';
      }

      if (selectedMeteringPoint && selectedMeteringPoint.meteringPointId && startDate && endDate && startDate <= endDate) {
        const rates = await fetchConsumptionRange(
          selectedMeteringPoint.customerId,
          activeUser.userId,
          selectedMeteringPoint.meteringPointId,
          viewPeriod,
          startOfDay(startDate).toISOString(),
          endOfDay(endDate).toISOString(),
        );

        if (rates) {
          setConsumption(rates);
          setConsumptionLoading(false);
        }
      }
    }
    async function fetchEliqForecastData(): Promise<void> {
      setForecastLoading(true);
      if (
        basicContract &&
        selectedMeteringPoint &&
        selectedMeteringPoint.meteringPointId &&
        startDate &&
        endDate &&
        startDate <= endDate &&
        startOfDay(endDate) >= startOfDay(subDays(new Date(), pastEstimateDays))
      ) {
        const eliqResolution = activeFilter === 'year' ? 'month' : 'day';

        const start = startOfDay(subDays(new UTCDate(), pastEstimateDays));
        let end = startOfDay(addDays(new UTCDate(endDate), 1));

        const dateDiff = differenceInCalendarDays(end, start);

        if (eliqResolution === 'day' && dateDiff > 31) {
          end = addDays(end, 31);
        }

        const forecastData = await fetchEliqForecast(
          selectedMeteringPoint?.customerId,
          activeUser.userId,
          selectedMeteringPoint?.meteringPointId,
          start?.toISOString(),
          end?.toISOString(),
          eliqResolution,
        );

        setForecast(forecastData);
        setForecastLoading(false);
      } else {
        setForecastLoading(false);
      }
    }
    async function fetchWeatherAndForecast(): Promise<void> {
      setWeatherLoading(true);
      let resolution = 'P1DT';

      if (activeFilter === 'year') {
        resolution = 'P1MT';
      }
      if (activeFilter === 'day') {
        resolution = dayConsumptionAs15min ? 'PT15M' : 'PT1H';
      }

      if (basicContract && startDate && endDate && basicContract.meteringpointAddress && startDate <= endDate) {
        const weather = await getWeatherAndForecast(
          generateAddress(basicContract.meteringpointAddress, false),
          startOfDay(startDate).toISOString(),
          endOfDay(endDate).toISOString(),
          resolution,
        );
        if (weather?.data && weather.data.length > 0) {
          setWeather(weather);
          setWeatherLoading(false);
        } else {
          setWeather(undefined);
          setWeatherLoading(false);
        }
      }
    }
    async function fetchSpotData(): Promise<void> {
      setSpotDataLoading(true);
      const FinnishTime = "yyyy-MM-dd'T'HH:mm:ss'Z'";

      let resolution = 'day';
      if (activeFilter === 'year') {
        resolution = 'month';
      }
      if (activeFilter === 'day') {
        resolution = dayConsumptionAs15min ? 'pt15m' : 'hour';
      }

      if (startDate && endDate && startDate <= endDate) {
        const data = await fetchNordPoolSpotData(format(startOfDay(startDate), FinnishTime), format(endOfDay(endDate), FinnishTime), resolution);
        setSpotData(data !== false ? data : undefined);
        setSpotDataLoading(false);
      }
    }
    async function fetchProfileData(): Promise<void> {
      const profileData = await fetchEliqHomeProfile(
        selectedMeteringPoint?.customerId ?? 0,
        activeUser.userId,
        selectedMeteringPoint?.meteringPointId ?? '',
      );

      if (profileData) {
        setEliqHomeProfile(profileData);
      }
    }
    async function fetchEliqSH(): Promise<void> {
      const similarHomes = await fetchEliqSimilarHomes(
        selectedMeteringPoint?.customerId ?? 0,
        activeUser.userId,
        selectedMeteringPoint?.meteringPointId ?? '',
      );
      if (similarHomes) {
        setEliqSh(similarHomes);
      }
    }
    async function fetchEliqSHC(): Promise<void> {
      if (startDate && endDate && startDate <= endDate) {
        const similarHomesConsumption = await fetchEliqSimilarHomesConsumption(
          selectedMeteringPoint?.customerId ?? 0,
          activeUser.userId,
          selectedMeteringPoint?.meteringPointId ?? '',
          addDays(startDate, 1).toISOString(),
          addDays(endDate, 1).toISOString(),
          activeFilter === 'year' ? 'month' : 'day',
        );
        if (similarHomesConsumption) {
          setEliqShc(similarHomesConsumption);
        }
      }
    }
    async function fetchEliqSHR(): Promise<void> {
      if (startDate && endDate && startDate <= endDate) {
        const similarHomesReport = await fetchEliqSimilarHomesReport(
          selectedMeteringPoint?.customerId ?? 0,
          activeUser.userId,
          selectedMeteringPoint?.meteringPointId ?? '',
          addDays(startDate, 1).toISOString(),
          addDays(endDate, 1).toISOString(),
        );
        if (similarHomesReport) {
          setEliqShr(similarHomesReport);
        }
      }
    }
    async function fetchEliqEUC(): Promise<void> {
      if (startDate && endDate && startDate <= endDate) {
        const EUC = await fetchEliqEuc(
          selectedMeteringPoint?.customerId ?? 0,
          activeUser.userId,
          selectedMeteringPoint?.meteringPointId ?? '',
          addDays(startDate, 1).toISOString(),
          subHours(addDays(endDate, 1), 1).toISOString(),
        );
        setEliqEucData(EUC ? EUC : undefined);
      }
    }
    async function checkEliqProfile(): Promise<void> {
      let completionValue = homeProfileCompletion;
      if (!completionValue || (completionValue < 100 && selectedMeteringPoint?.meteringPointId)) {
        const completion = await fetchEliqHomeProfileCompletion(
          selectedMeteringPoint?.customerId ?? 0,
          activeUser.userId,
          selectedMeteringPoint?.meteringPointId ?? '',
        );

        if (completion && !isNaN(completion.homeProfileCompletion)) {
          completionValue = completion.homeProfileCompletion;
          setEliqHomeProfileCompletion(completionValue);
        }
      }
      if (completionValue !== undefined) {
        fetchProfileData();
      }
      if (completionValue && completionValue > 0) {
        if (activeFilter === 'month' || activeFilter === 'year') {
          fetchEliqSH();
          fetchEliqSHC();
          fetchEliqSHR();
        }
        if (activeFilter === 'month') {
          fetchEliqEUC();
        }
      }
    }
    async function fetchWeightedAverageSpotPrice(): Promise<void> {
      setWeightedAvgSpotPriceLoading(true);

      const scope = 'period';

      if (startDate && endDate && startDate <= endDate) {
        const data = await fetchWeightedAverage(selectedMeteringPoint, scope, activeUser.userId, startDate);
        setWeightedAvgSpotPrice(data);
        setWeightedAvgSpotPriceLoading(false);
      }
    }

    async function fetchConsumptionAvg(): Promise<void> {
      setConsumptionAvgLoading(true);

      if (startDate && endDate && startDate <= endDate) {
        const data = await fetchConsumptionAverage(selectedMeteringPoint, activeUser.userId, startDate, endDate, 'Hour');
        setConsumptionAvg(data);
        setConsumptionAvgLoading(false);
      }
    }

    // update data
    if (
      (selectedMeteringPoint || basicContract) &&
      !(
        activeFilter === prevControls.activeFilter &&
        startDate?.toISOString() === prevControls.startDate?.toISOString() &&
        endDate?.toISOString() === prevControls.endDate?.toISOString() &&
        selectedMeteringPoint?.meteringPointId === prevControls.meteringPointId &&
        basicContract?.contractCode === prevControls.contractCode &&
        dayConsumptionAs15min === prevControls.dayConsumptionAs15min
      )
    ) {
      dispatchConsumption(['changeDataLoading', true]);
      setConsumption(undefined);
      setConsumptionLoading(false);
      setForecast(undefined);
      setForecastLoading(false);
      setWeather(undefined);
      setWeatherLoading(false);
      setSpotData(undefined);
      setSpotDataLoading(false);
      setEliqEucData(undefined);
      setEliqShc(undefined);
      setEliqShr(undefined);
      setWeightedAvgSpotPrice(undefined);
      setWeightedAvgSpotPriceLoading(false);
      setConsumptionAvg(undefined);
      setConsumptionAvgLoading(false);

      setEliqHomeProfile(undefined);
      setEliqHomeProfileCompletion(undefined);
      setEliqSh(undefined);

      fetchConsumptionData();
      fetchConsumptionAvg();

      if (type === 'single' && (!weatherLoading || (!weather && weatherLoading))) {
        fetchWeatherAndForecast();
      }

      if (
        type === 'single' &&
        (!nordPoolData ||
          !(startDate?.toISOString() === prevControls.startDate?.toISOString() && endDate?.toISOString() === prevControls.endDate?.toISOString()) ||
          prevControls.dayConsumptionAs15min !== dayConsumptionAs15min)
      ) {
        fetchSpotData();
        if (config?.weightedAverage === true) {
          fetchWeightedAverageSpotPrice();
        }
      }

      if (type === 'single' && !productionContract && !b2bUser && !forecastLoading) {
        if (activeFilter !== 'day') {
          fetchEliqForecastData();
        }

        if (!b2bUser) {
          checkEliqProfile();
        }
        if (!(startDate?.toISOString() === prevControls.startDate?.toISOString() && endDate?.toISOString() === prevControls.endDate?.toISOString())) {
          if (activeFilter === 'month') {
            fetchEliqEUC();
          }
          if (activeFilter === 'month' || activeFilter === 'year') {
            fetchEliqSHC();
            fetchEliqSHR();
          }
        }
      }
      setPrevControls({
        activeFilter,
        startDate,
        endDate,
        meteringPointId: selectedMeteringPoint?.meteringPointId ?? '',
        contractCode: basicContract?.contractCode ?? '',
        meteringPointGroup: undefined,
      });
    }
    if (!spotDataLoading && !nordPoolData) {
      fetchSpotData();
    }
  }, [activeFilter, startDate, endDate, dayConsumptionAs15min]);

  React.useEffect(() => {
    if (consumption && !consumptionLoading) {
      dispatchConsumption(['changeConsumption', consumption]);
    }
    if (forecast && !forecastLoading) {
      dispatchConsumption(['changeForecast', forecast]);
    }
    if (weather && !weatherLoading) {
      dispatchConsumption(['changeWeather', weather]);
    }
    if (spotData && !spotDataLoading) {
      dispatchConsumption(['changeSpot', spotData]);
    }
    if (weightedAvgSpotPrice && !weightedAvgSpotPriceLoading) {
      dispatchConsumption(['changeWeightedAvg', weightedAvgSpotPrice]);
    }
    if (consumptionAvg && !consumptionAvgLoading) {
      dispatchConsumption(['changeConsumptionAvg', consumptionAvg]);
    }
  }, [
    consumption,
    consumptionLoading,
    forecast,
    forecastLoading,
    weather,
    weatherLoading,
    spotData,
    spotDataLoading,
    weightedAvgSpotPrice,
    weightedAvgSpotPriceLoading,
    consumptionAvg,
    consumptionAvgLoading,
  ]);

  React.useEffect(() => {
    dispatchConsumption(['changeEliqProfileCompletion', eliqHomeProfileCompletion]);
    dispatchConsumption(['changeEliqProfile', eliqHomeProfile]);
    dispatchConsumption(['changeEliqEuc', eliqEucData]);
    dispatchConsumption(['changeEliqSimilarHomes', eliqSh]);
    dispatchConsumption(['changeEliqSimilarHomesConsumption', eliqShc]);
    dispatchConsumption(['changeEliqSimilarHomesReport', eliqShr]);
  }, [eliqHomeProfileCompletion, eliqHomeProfile, eliqEucData, eliqSh, eliqShc, eliqShr]);

  return children;
};

export default ConsumptionPageWrapper;
